import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { setExpandAllAction } from '../../../store/actions/actions';
import NestedTableRow from '../../../components/tables/nestedTable/NestedTableRow/NestedTableRow';
import ExpandedMetersRow from '../../MetersExpandedRow/MetersExpandedRow';
import MeterModal from '../../../components/modals/MeterModal/MeterModal';
import ExpandButton from '../../../components/ExpandButton/ExpandButton';
import LayeredTable from '../../../components/tables/layeredTable/LayeredTable/LayeredTable';
import styles from '../Meters.module.scss';
import queries from '../../../queries/queries';
import deleteMeter from '../../../requests/deleteMeters';

const properties = [
  { name: 'meterCode', header: 'Номер счетчика', flex: 1, highlight: true },
  { name: 'type.name', header: 'Тип', flex: 1 },
  { name: 'coefficient', header: 'Коэффициент', flex: 1 },
  { name: 'rentalHouseTariff.value', header: 'Тариф', flex: 1 },
  { name: 'lastReading', header: 'Последнее показание', flex: 1 },
  { name: 'currentReading', header: 'Текущее показание', flex: 1 },
];

const { confirm } = Modal;

const FacilityAndRoomsMeters = ({ facilityWithMeters }) => {
  const queryClient = useQueryClient();

  const dispatch = useDispatch();
  const expandedAll = useSelector((state) => state.officesStore.expandAllRows);
  const { CanCreateOffice, CanDeleteOffice } = useSelector(
    (store) => store.authentication.userInfo
  );

  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [officeData, setOfficeData] = useState(null);
  const [meterFormInitialValues, setMeterFormInitialValues] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const closeMeterModal = () => {
    setEditModalIsOpen(false);
    setOfficeData(null);
    setMeterFormInitialValues(null);
  };

  const handleEditRow = (_, data, office) => {
    setEditModalIsOpen(true);
    setOfficeData(office);
    setMeterFormInitialValues(data);
  };

  const handleDeleteRow = (meter) => {
    confirm({
      title: `Вы уверены, что хотите удалить счетчик ${meter.meterCode || ''}?`,
      content: 'Это действие нельзя будет отменить',
      onOk: async () => {
        const res = await deleteMeter(meter.id);

        if (res.result === 'Ok') {
          queryClient.invalidateQueries(queries.meters);
        }
      },
      onCancel() {},
    });
  };

  const updateMetersData = () => queryClient.invalidateQueries(queries.meters);

  useEffect(() => {
    if (expandedAll) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [expandedAll]);

  const clickToggleExpand = () => {
    dispatch(setExpandAllAction(false));
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={styles.facilityMeters}>
      <div className={styles.nestedTableRow} style={{ background: '#F3F7FC' }}>
        <div className={styles.rowHeader}>
          <div
            className={cn(styles.blockHighlight, {
              [styles.blockHighlightClickable]: !!facilityWithMeters.meters,
            })}
            onClick={facilityWithMeters.meters && clickToggleExpand}
          >
            <span className={styles.bold}>{facilityWithMeters.name}</span>
            {facilityWithMeters.house &&
              `/${facilityWithMeters.house.fullAddress}`}
          </div>
          {facilityWithMeters.meters && (
            <ExpandButton
              clickToggleExpand={clickToggleExpand}
              isExpanded={isExpanded}
            />
          )}
        </div>
        {isExpanded && facilityWithMeters.meters && (
          <LayeredTable
            rootClassName={styles.tableContainer}
            data={facilityWithMeters.meters}
            rowDataPropertiesArray={properties}
            lightTheme
            expandedRowContent={(meter) => <ExpandedMetersRow meter={meter} />}
            onEditRow={handleEditRow}
            onDeleteRow={handleDeleteRow}
            isEditing
            editable={CanCreateOffice}
            deletable={CanDeleteOffice}
            expandable="start"
          />
        )}
      </div>
      {facilityWithMeters.offices?.map((office) => (
        <NestedTableRow
          key={office.id}
          data={office.meters}
          rowDataPropertiesArray={properties}
          innerLightTheme={true}
          expandedRowContent={(meter) => <ExpandedMetersRow meter={meter} />}
          onEditRow={handleEditRow}
          isEditing
          officeData={office}
          onDeleteRow={handleDeleteRow}
        />
      ))}
      {editModalIsOpen && (
        <MeterModal
          facilityData={facilityWithMeters}
          officeData={officeData}
          visible={editModalIsOpen}
          onClose={closeMeterModal}
          initialValues={meterFormInitialValues}
          onOk={updateMetersData}
        />
      )}
    </div>
  );
};

export default FacilityAndRoomsMeters;
